const REQUEST_TIMEOUT_MS = 3_000

export type Answer = { ok: true; status: number; data: unknown } | { ok: false; status: number | null }

/** The three REST calls the display makes, all with the receiver grant and
 *  nothing else: never a cookie or a cashier header, even in a browser that
 *  has a Reactivate login. A timeout or network error is `status: null`,
 *  which means unknown, never success. */
export function createReceiverApi(apiUrl: string, placeId: number, token: string) {
  const request = async (method: 'GET' | 'POST', path: string, body?: unknown): Promise<Answer> => {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    try {
      const response = await fetch(new URL(`/places/${placeId}/device-requests${path}`, apiUrl).toString(), {
        method,
        credentials: 'omit',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', 'X-Device-Receiver': token },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
      })
      if (!response.ok) return { ok: false, status: response.status }
      return { ok: true, status: response.status, data: await response.json() }
    } catch {
      return { ok: false, status: null }
    } finally {
      clearTimeout(timer)
    }
  }

  return {
    latest: (userId: number) => request('GET', `?latest_only=true&target_type=customer_display&target_id=${userId}`),
    delivered: (id: number) => request('POST', `/${id}/delivered`, {}),
    fulfilled: (id: number) => request('POST', `/${id}/outcome`, { outcome: 'fulfilled' }),
  }
}

export function isRefusal(answer: Answer): boolean {
  return !answer.ok && (answer.status === 401 || answer.status === 403)
}
